import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

@Component({
  selector: 'app-custom-date-picker',
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatePickerComponent),
      multi: true,
    },
  ],
  standalone: false
})
export class CustomDatePickerComponent implements ControlValueAccessor {
  rawInputDisplay = '';
  private isUserInput = false;

  @ViewChild('nativeDateInput') nativeDateInput?: ElementRef<HTMLInputElement>;
  @Input() key = 0;
  @Input() label = '';
  @Input() id = '0';
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() inputValid: boolean | undefined = true;
  private _rawInput = '';
  @Input() set rawInput(value: string | null | undefined) {
    const safeValue = value ?? '';
    if (this._rawInput !== safeValue) {
      this._rawInput = safeValue;
      this.updateRawInputDisplay(safeValue);
    }
  }

  @Input() isApproximateYearInput = false;
  @Input() isApproximate = false;
  @Input() isYearOnly = false;

  @Output() approxChanged = new EventEmitter<boolean>();
  @Output() yearOnlyChanged = new EventEmitter<boolean>();
  @Output() valueChange = new EventEmitter<string>();

  constructor(private readonly cdRef: ChangeDetectorRef) {
    // silence is golden
  }

  private onChange: (value: string) => void = () => {
    // silence is golden
  };
  private onTouched: () => void = () => {
    // silence is golden
  };

  writeValue(value: string): void {
    if (value && typeof value === 'string') {
      this.updateRawInputDisplay(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onRawInput(event: Event) {
    const input = (event.target as HTMLInputElement)?.value ?? '';
    this.handleInputChange(input);
  }

  triggerNativePicker() {
    this.nativeDateInput?.nativeElement.click();
  }

  onNativeDateSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target?.value) {
      const [yyyy, mm, dd] = target.value.split('-');
      const formatted = `${dd}.${mm}.${yyyy}`;
      this.handleInputChange(formatted);
    }
  }

  private handleInputChange(input: string) {
    const trimmed = input.trim();
    this.isUserInput = true; // Wir unterdrücken gleich das Update

    const fullDateMatch = /^\d{2}\.\d{2}\.\d{4}$/.test(trimmed);
    const monthYearMatch = /^\d{2}\.\d{4}$/.test(trimmed);
    const yearOnlyMatch = /^\d{4}$/.test(trimmed);

    if (fullDateMatch) {
      const [dd, mm, yyyy] = trimmed.split('.');
      this.emitValue(`${yyyy}-${mm}-${dd}`, false, false);
    } else if (monthYearMatch) {
      const [mm, yyyy] = trimmed.split('.');
      this.emitValue(`${yyyy}-${mm}-01`, true, false);
    } else if (yearOnlyMatch) {
      const yyyy = trimmed;
      this.emitValue(`${yyyy}-01-01`, true, true);
    } else {
      this.emitValue('', false, false);
    }

    this.rawInputDisplay = trimmed;
  }


  private emitValue(value: string, approx: boolean, yearOnly: boolean) {
    this.isApproximate = approx;
    this.isYearOnly = yearOnly;

    this.onChange(value);
    this.valueChange.emit(value);
    this.approxChanged.emit(approx);
    this.yearOnlyChanged.emit(yearOnly);
    this.cdRef.markForCheck();
  }

  private updateRawInputDisplay(value: string) {
    if (this.isUserInput) {
      this.isUserInput = false;
      return;
    }

    if (!value) {
      this.rawInputDisplay = '';
      return;
    }

    if (this.isYearOnly && /^\d{4}-01-01$/.test(value)) {
      const [yyyy] = value.split('-');
      this.rawInputDisplay = yyyy;
      return;
    }

    const fullDateMatch = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const monthYearMatch = /^\d{4}-\d{2}-01$/.test(value);

    if (fullDateMatch) {
      const [yyyy, mm, dd] = value.split('-');
      this.rawInputDisplay = `${dd}.${mm}.${yyyy}`;
    } else if (monthYearMatch) {
      const [yyyy, mm] = value.split('-');
      this.rawInputDisplay = `${mm}.${yyyy}`;
    } else {
      this.rawInputDisplay = '';
    }
  }

  getDisplayValue(): string {
    if (this.isApproximateYearInput && this.isYearOnly) {
      return this.rawInputDisplay.split('.')[2] ?? this.rawInputDisplay; // nimmt nur Jahr
    }
    return this.rawInputDisplay;
  }


}
